/**
 * Navigation Utility Functions
 * 
 * Shared utilities for navigation components and hooks.
 * 
 * @module lib/navigation/utils
 */

import { buildUrl } from '../queryParams';

/**
 * Build URL with current search params
 * 
 * Parses the target URL to extract any explicit query params,
 * then merges with preserved params from current URL.
 * 
 * @param to - Target URL (may include query string)
 * @param searchParams - Current URLSearchParams to preserve
 * @returns Full URL with merged query parameters
 * 
 * @example
 * ```tsx
 * const [searchParams] = useSearchParams();
 * // Current URL: ?tenant-handle=drc&club-id=2
 * 
 * buildUrlWithCurrentParams('/booking', searchParams);
 * // => '/booking?tenant-handle=drc&club-id=2'
 * 
 * buildUrlWithCurrentParams('/booking?payment-provider=square', searchParams);
 * // => '/booking?payment-provider=square&tenant-handle=drc&club-id=2'
 * ```
 */
export function buildUrlWithCurrentParams(
  to: string, 
  searchParams: URLSearchParams
): string {
  // Split the URL into path and query string
  const [path, queryString] = to.split('?');
  
  // Parse any explicit params from the target URL
  const explicitParams: Record<string, string> = {};
  if (queryString) {
    const urlParams = new URLSearchParams(queryString);
    urlParams.forEach((value, key) => {
      explicitParams[key] = value;
    });
  }
  
  // Build URL with merged params
  return buildUrl(path, explicitParams, searchParams);
}
